import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Camera, AlertCircle, CheckCircle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import Webcam from 'react-webcam';
import jsQR from 'jsqr';
import api from '../utils/api';

const QRScannerGun = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scannerToken = searchParams.get('token'); // Changed from orgPin to token
  
  const [cameraActive, setCameraActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const [validatingToken, setValidatingToken] = useState(true); // Changed from validatingPin
  const [tokenValid, setTokenValid] = useState(false); // Changed from pinValid
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastCheckedIn, setLastCheckedIn] = useState(null);
  const [scanState, setScanState] = useState('idle'); // idle, detecting, success, warning, error
  const [lastError, setLastError] = useState('');
  const [orgName, setOrgName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  
  const webcamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const scanningRef = useRef(false); // Track scanning state in ref for interval
  const cooldownRef = useRef(false); // Track if we're in cooldown period
  const lastScannedQRRef = useRef(null); // Track last scanned QR to prevent duplicates
  const lastScanTimeRef = useRef(0); // Track when we last scanned

  // Validate token on mount
  useEffect(() => {
    validateScannerToken();
  }, [scannerToken]);

  const validateScannerToken = async () => {
    if (!scannerToken) {
      toast.error('Invalid scanner link - missing token');
      setValidatingToken(false);
      return;
    }

    try {
      
      const response = await api.post('/public/check-in/validate-scanner-token', { token: scannerToken });
      
      
      if (response.data.valid) {
        setTokenValid(true);
        setOrgName(response.data.organizationName);
        setExpiresAt(response.data.expiresAt);
        toast.success(`Scanner ready for ${response.data.organizationName}`);
      } else {
        console.error('❌ Token invalid:', response.data.message);
        toast.error(response.data.message || 'Invalid scanner token');
        setTokenValid(false);
      }
    } catch (error) {
      console.error('❌ Token validation error:', error);
      
      let errorMsg = 'Scanner token validation failed';
      try {
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (typeof detail === 'string') {
            errorMsg = detail;
          } else if (Array.isArray(detail)) {
            errorMsg = detail.map(e => {
              if (typeof e === 'string') return e;
              if (e.msg) return String(e.msg);
              return 'Validation error';
            }).join(', ');
          } else if (detail && typeof detail === 'object') {
            errorMsg = detail.msg ? String(detail.msg) : 'Validation failed';
          }
        } else if (error.message) {
          errorMsg = String(error.message);
        }
      } catch (parseError) {
        console.error('Error parsing error message:', parseError);
      }
      
      toast.error(errorMsg);
      setTokenValid(false);
    } finally {
      setValidatingToken(false);
    }
  };

  const startCamera = async () => {
    setCameraActive(true);
    
    // Auto-start scanning when camera starts
    setTimeout(() => {
      setScanning(true);
      scanningRef.current = true; // Set ref too for interval
      
      const intervalId = setInterval(() => {
        scanQRCode();
      }, 500);
      
      scanIntervalRef.current = intervalId;
      
      toast.success('Scanning active - point at QR code');
    }, 1500); // Increased delay to ensure camera is ready
  };

  const stopCamera = () => {
    setScanning(false);
    scanningRef.current = false; // Update ref too
    
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setCameraActive(false);
  };

  const scanQRCode = () => {
    if (!webcamRef.current || !scanningRef.current || cooldownRef.current) {
      return;
    }

    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      return;
    }

    // Convert base64 image to image data
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });

      if (code?.data) {
        
        // Check if we're in cooldown OR if this is the same QR code we just scanned
        const now = Date.now();
        const timeSinceLastScan = now - lastScanTimeRef.current;
        const isSameQR = lastScannedQRRef.current === code.data;
        
        // Only process if: not in cooldown AND (different QR OR enough time passed)
        if (!cooldownRef.current && (!isSameQR || timeSinceLastScan > 3000)) {
          // IMMEDIATELY enter cooldown and STOP the scanning interval
          cooldownRef.current = true;
          scanningRef.current = false;
          lastScannedQRRef.current = code.data;
          lastScanTimeRef.current = now;
          
          // STOP the interval completely during cooldown
          if (scanIntervalRef.current) {
            clearInterval(scanIntervalRef.current);
            scanIntervalRef.current = null;
          }
          
          setScanState('detecting'); // Blue state
          handleQRCodeDetected(code.data);
        } else {
        }
      } else {
        setScanState('idle'); // Grey state - no QR code in view
      }
    };
    
    img.onerror = (err) => {
      console.error('[SCAN] Image load error:', err);
    };
    
    img.src = imageSrc;
  };

  const handleQRCodeDetected = async (qrData) => {
    // Already stopped scanning in scanQRCode() to prevent duplicate scans
    setScanning(false);
    
    
    // Vibrate if supported
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }

    try {
      // Extract confirmation code from QR data
      let confirmationCode = qrData;
      
      // Handle different QR code formats
      if (qrData.includes('/check-in/')) {
        // Format: https://domain.com/check-in/BF-ABC123?org=...
        const urlParts = qrData.split('/check-in/')[1];
        confirmationCode = urlParts.split('?')[0]; // Get BF-ABC123
      } else if (qrData.includes('confirmation=')) {
        // Format: ...?confirmation=BF-ABC123&...
        confirmationCode = qrData.split('confirmation=')[1].split('&')[0];
      } else if (qrData.startsWith('BF-')) {
        // Already just the code
        confirmationCode = qrData;
      }
      

      // Check in the customer using the scanner token - PUBLIC endpoint
      const response = await api.post('/public/check-in/scan', {
        confirmationCode,
        scannerToken: scannerToken // Use token instead of PIN
      });
      

      const booking = response.data.booking;
      const alreadyCheckedIn = response.data.alreadyCheckedIn;
      
      // Set appropriate state based on whether already checked in
      if (alreadyCheckedIn) {
        setScanState('warning'); // Yellow/orange state for already checked in
      } else {
        setScanState('success'); // Green state for new check-in
      }
      
      // Show success modal with appropriate message
      setLastCheckedIn({
        customerName: booking.customerName,
        customerEmail: booking.customerEmail,
        serviceName: booking.serviceName,
        alreadyCheckedIn: alreadyCheckedIn
      });
      setShowSuccessModal(true);
      
      // Add to recent scans
      setRecentScans(prev => [{
        id: booking.id,
        confirmationCode: booking.confirmationCode,
        customerName: booking.customerName,
        serviceName: booking.serviceName,
        checkedInAt: new Date().toISOString(),
        status: alreadyCheckedIn ? 'already_checked_in' : 'success'
      }, ...prev.slice(0, 9)]); // Keep last 10
      
      const message = alreadyCheckedIn 
        ? `⚠️ Already checked in: ${booking.customerName}`
        : `✅ ${booking.customerName} checked in!`;
      toast.success(message);
      
      // Start cooldown countdown (4 seconds)
      const cooldownDuration = 4;
      setCooldownSeconds(cooldownDuration);
      
      const countdownInterval = setInterval(() => {
        setCooldownSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Auto-hide modal and resume scanning after cooldown
      setTimeout(() => {
        setShowSuccessModal(false);
        setScanState('idle');
        setScanning(true);
        scanningRef.current = true;
        cooldownRef.current = false; // Exit cooldown
        setCooldownSeconds(0);
        
        // RESTART the scanning interval
        const intervalId = setInterval(() => {
          scanQRCode();
        }, 500);
        scanIntervalRef.current = intervalId;
      }, cooldownDuration * 1000);
      
    } catch (error) {
      console.error('❌ Check-in error:', error);
      console.error('Error response:', error.response?.data);
      
      // Extract error message - BULLETPROOF conversion to string
      let errorMsg = 'Check-in failed';
      
      try {
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          
          if (typeof detail === 'string') {
            errorMsg = detail;
          } else if (Array.isArray(detail)) {
            // Pydantic validation error array
            errorMsg = detail.map(e => {
              if (typeof e === 'string') return e;
              if (e.msg) return String(e.msg);
              return 'Validation error';
            }).join(', ');
          } else if (detail && typeof detail === 'object') {
            errorMsg = detail.msg ? String(detail.msg) : 'Validation failed';
          }
        } else if (error.message) {
          errorMsg = String(error.message);
        }
      } catch (parseError) {
        console.error('Error parsing error message:', parseError);
        errorMsg = 'Check-in failed';
      }
      
      
      // Ensure everything is a string before setting state
      const errorString = String(errorMsg);
      setLastError(errorString);
      setScanState('error');
      toast.error(errorString);
      
      // Add failed scan to recent scans - ensure error is string
      setRecentScans(prev => [{
        id: Date.now(),
        confirmationCode: String(qrData).substring(0, 20),
        status: 'error',
        error: errorString, // Already a string
        checkedInAt: new Date().toISOString()
      }, ...prev.slice(0, 9)]);
      
      // Start cooldown countdown for errors too (4 seconds)
      const cooldownDuration = 4;
      setCooldownSeconds(cooldownDuration);
      
      const countdownInterval = setInterval(() => {
        setCooldownSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Resume scanning after 3 seconds
      setTimeout(() => {
        setScanState('idle');
        setLastError('');
        setScanning(true);
        scanningRef.current = true;
        cooldownRef.current = false; // Exit cooldown on error too
        setCooldownSeconds(0);
        
        // RESTART the scanning interval
        const intervalId = setInterval(() => {
          scanQRCode();
        }, 500);
        scanIntervalRef.current = intervalId;
      }, cooldownDuration * 1000);
    }
  };

  useEffect(() => {
    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, []);

  if (validatingToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600">Validating scanner...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Scanner Link</h1>
          <p className="text-gray-600 mb-6">
            This scanner link is invalid or expired. Please contact your administrator for a new scanner link.
          </p>
          <button
            onClick={() => navigate('/admin')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">QR Scanner Gun</h1>
          <p className="text-sm text-gray-400 mt-1">{orgName || 'Loading...'}</p>
          {expiresAt && (
            <p className="text-xs text-yellow-400 mt-1">
              Expires: {new Date(expiresAt).toLocaleString()}
            </p>
          )}
        </div>
        <button
          onClick={() => navigate('/admin')}
          className="p-2 text-gray-400 hover:text-white transition"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Camera View */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {!cameraActive ? (
          <div className="text-center">
            <Camera className="w-24 h-24 text-gray-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-4">Ready to Scan</h2>
            <p className="text-gray-400 mb-8 max-w-md">
              Start your camera to begin scanning customer QR codes. Point camera at the ticket's QR code.
            </p>
            <button
              onClick={startCamera}
              className="px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition shadow-lg"
            >
              <Camera className="w-6 h-6 inline mr-2" />
              Start Camera
            </button>
          </div>
        ) : (
          <div className="w-full max-w-3xl space-y-4">
            {/* Camera Feed - REACT WEBCAM */}
            <div className="relative bg-black rounded-lg overflow-hidden shadow-2xl">
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{
                  facingMode: 'environment',
                  width: 1280,
                  height: 720
                }}
                className="w-full h-auto"
                style={{ minHeight: '500px', maxHeight: '70vh' }}
              />
              
              {/* Scanning frame overlay with dynamic colors */}
              {scanning && (
                <>
                  {/* Status indicator */}
                  <div className={`absolute top-4 left-4 px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 z-10 ${
                    scanState === 'idle' ? 'bg-gray-500' :
                    scanState === 'detecting' ? 'bg-blue-500 animate-pulse' :
                    scanState === 'success' ? 'bg-green-500' :
                    scanState === 'warning' ? 'bg-yellow-500' :
                    'bg-red-500'
                  } text-white`}>
                    <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                    {scanState === 'idle' && 'Scanning...'}
                    {scanState === 'detecting' && 'QR Code Found!'}
                    {scanState === 'success' && 'Checked In!'}
                    {scanState === 'warning' && 'Already Checked In'}
                    {scanState === 'error' && 'Failed'}
                  </div>
                  
                  {/* Cooldown indicator - shows when in cooldown */}
                  {cooldownSeconds > 0 && (
                    <div className={`absolute top-20 left-4 px-6 py-3 rounded-lg font-bold text-lg shadow-xl z-10 flex items-center gap-3 ${
                      scanState === 'success' ? 'bg-green-600 text-white' :
                      scanState === 'warning' ? 'bg-yellow-500 text-white' :
                      scanState === 'error' ? 'bg-red-600 text-white' :
                      'bg-blue-600 text-white'
                    }`}>
                      <div className="w-10 h-10 rounded-full border-4 border-white flex items-center justify-center">
                        <span className="text-xl">{cooldownSeconds}</span>
                      </div>
                      <span>Cooldown...</span>
                    </div>
                  )}
                  
                  {/* Scanning frame with dynamic border color */}
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className={`relative w-72 h-72 border-4 rounded-lg shadow-lg transition-colors duration-300 ${
                      scanState === 'idle' ? 'border-gray-400' :
                      scanState === 'detecting' ? 'border-blue-400' :
                      scanState === 'success' ? 'border-green-400' :
                      scanState === 'warning' ? 'border-yellow-400' :
                      'border-red-400'
                    }`}>
                      <div className={`absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 ${
                        scanState === 'idle' ? 'border-gray-400' :
                        scanState === 'detecting' ? 'border-blue-400' :
                        scanState === 'success' ? 'border-green-400' :
                        scanState === 'warning' ? 'border-yellow-400' :
                        'border-red-400'
                      }`}></div>
                      <div className={`absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 ${
                        scanState === 'idle' ? 'border-gray-400' :
                        scanState === 'detecting' ? 'border-blue-400' :
                        scanState === 'success' ? 'border-green-400' :
                        scanState === 'warning' ? 'border-yellow-400' :
                        'border-red-400'
                      }`}></div>
                      <div className={`absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 ${
                        scanState === 'idle' ? 'border-gray-400' :
                        scanState === 'detecting' ? 'border-blue-400' :
                        scanState === 'success' ? 'border-green-400' :
                        scanState === 'warning' ? 'border-yellow-400' :
                        'border-red-400'
                      }`}></div>
                      <div className={`absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 ${
                        scanState === 'idle' ? 'border-gray-400' :
                        scanState === 'detecting' ? 'border-blue-400' :
                        scanState === 'success' ? 'border-green-400' :
                        scanState === 'warning' ? 'border-yellow-400' :
                        'border-red-400'
                      }`}></div>
                    </div>
                  </div>
                  
                  {/* Error message display */}
                  {scanState === 'error' && lastError && (
                    <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg max-w-md text-center z-10">
                      <p className="font-semibold mb-1">Scan Failed</p>
                      <p className="text-sm">{lastError}</p>
                    </div>
                  )}
                </>
              )}
              
              {/* Instructions overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                <p className="text-white text-center text-lg font-semibold">
                  Point camera at QR code on customer's ticket
                </p>
              </div>
            </div>

            {/* Control Button */}
            <button
              onClick={stopCamera}
              className="w-full py-4 bg-red-600 text-white text-lg font-semibold rounded-lg hover:bg-red-700 transition shadow-lg"
            >
              <X className="w-5 h-5 inline mr-2" />
              Stop Camera
            </button>
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && lastCheckedIn && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl p-8 max-w-md w-full text-center transform animate-in zoom-in-95 duration-300 ${
            lastCheckedIn.alreadyCheckedIn 
              ? 'bg-yellow-50 border-4 border-yellow-400' 
              : 'bg-white'
          }`}>
            {lastCheckedIn.alreadyCheckedIn ? (
              <>
                <AlertCircle className="w-20 h-20 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Already Checked In</h2>
              </>
            ) : (
              <>
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Checked In!</h2>
              </>
            )}
            <p className="text-2xl font-semibold text-gray-700 mb-1">
              {lastCheckedIn.customerName || lastCheckedIn.customerEmail}
            </p>
            {lastCheckedIn.serviceName && (
              <p className="text-gray-600 mb-4">{lastCheckedIn.serviceName}</p>
            )}
            {cooldownSeconds > 0 && (
              <div className="mt-6">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full border-4 mb-2 ${
                  lastCheckedIn.alreadyCheckedIn 
                    ? 'bg-yellow-100 border-yellow-500' 
                    : 'bg-green-100 border-green-500'
                }`}>
                  <span className={`text-3xl font-bold ${
                    lastCheckedIn.alreadyCheckedIn ? 'text-yellow-600' : 'text-green-600'
                  }`}>{cooldownSeconds}</span>
                </div>
                <p className="text-sm text-gray-500">Ready to scan next ticket in...</p>
                <p className="text-xs text-gray-400 mt-3">
                  💡 Customer can refresh their ticket to see check-in status
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Scans Sidebar */}
      {recentScans.length > 0 && (
        <div className="bg-gray-800 border-t border-gray-700 px-6 py-4 max-h-64 overflow-y-auto">
          <h3 className="text-sm font-semibold text-gray-400 mb-3">Recent Scans</h3>
          <div className="space-y-2">
            {recentScans.map((scan, idx) => (
              <div
                key={idx}
                className={`p-3 rounded-md ${
                  scan.status === 'success' ? 'bg-green-900/30 border border-green-700' :
                  scan.status === 'already_checked_in' ? 'bg-yellow-900/30 border border-yellow-700' :
                  'bg-red-900/30 border border-red-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  {scan.status === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  ) : scan.status === 'already_checked_in' ? (
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    {scan.status === 'error' ? (
                      <>
                        <p className="text-sm font-semibold text-white">Scan Failed</p>
                        <p className="text-xs text-red-400">{String(scan.error || 'Unknown error')}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-white truncate">
                          {scan.status === 'already_checked_in' ? '⚠️ ' : ''}{scan.customerName}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{scan.serviceName}</p>
                        <p className="text-xs text-gray-500">{scan.confirmationCode}</p>
                      </>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(scan.checkedInAt).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScannerGun;
